import { RecommendSearchRequest } from './recommendSearch/types';

// Added Algolia Search Types

// This fancy thing allows us to use Omit correctly with a distributed union.
// Credit and further reading here: https://tkdodo.eu/blog/omit-for-discriminated-unions-in-type-script
type DistributiveOmit<T, K extends keyof T> = T extends any ? Omit<T, K> : never;

// Recommend
export type RecommendSearchOptions = DistributiveOmit<
  RecommendSearchRequest,
  'indexName' | 'threshold'
> & {
  threshold?: number;
};

// Fitment
export interface FitmentSearchOptions {
  resultsPerPage: number;
  vehicleIds?: number[];
  facets?: string[];
  filters?: string;
}
export interface FitmentSearchParams {
  hitsPerPage: number;
  vehicleIds?: number[];
  facets?: string[];
  filters?: string;
}
export interface FitmentHit {
  shopifysku: string;
  vehicleid: string;
  criteria_values?: string;
  ktypnr?: string;
  make?: string;
  model?: string;
  series?: string;
  universal?: number;
  panote?: string;
}

// Product
export interface ProductSearchParams {
  query?: string;
  hitsPerPage?: number;
  page?: number;
  facets?: string[];
  facetFilters?: string[][];
  filters?: string;
  sumOrFiltersScores?: boolean;
  sortBy?: SortOption;
  resultsPerPage?: number;
  vehicleIds?: number[] | string[];
  optionalFilters?: string[][];
  enableAutocomplete?: boolean;
  userToken?: string;
  enablePersonalization?: boolean;
  typoTolerance?: boolean;
  restrictSearchableAttributes?: string[];
  facetMaps?: Record<string, string>;
}

export interface MappedProductSearchParams {
  indexName: string;
  query?: string;
  hitsPerPage?: number;
  page?: number;
  facets?: string[];
  facetFilters?: string[][];
  filters?: string;
  sumOrFiltersScores?: boolean;
  optionalFilters?: string[][];
  clickAnalytics?: boolean;
  userToken?: string;
  enablePersonalization?: boolean;
  typoTolerance?: boolean;
  restrictSearchableAttributes?: string[];
  facetMaps?: Record<string, string>;
}

// Vehicle
export interface VehicleSearchOptions {
  query?: string;
  resultsPerPage: number;
  vehicleIds?: number[];
  facets?: string[];
  filters?: string;
  facetFilters?: string[];
}

export interface VehicleSearchParams {
  query?: string;
  hitsPerPage: number;
  vehicleIds?: number[];
  facets?: string[];
  filters?: string;
  facetFilters?: string[];
}

// General
export type AlgoliaResult<T> = {
  hits: T[];
  hitsPerPage: number;
  nbHits: number;
  nbPages: number;
  page: number;
  query: string;
  index: string;
  facets?: FacetCounts;
  queryID?: string;
};

export interface AlgoliaSearchResponse<T = DefaultItems> {
  results: T[];
}

type FacetCounts = Record<string, Record<string, number>>;

export type AlgoliaFitmentResult = AlgoliaResult<FitmentHit>;
export type AlgoliaProductResult = AlgoliaResult<ProductRecord>;
export type AlgoliaPopularSearchResult = AlgoliaResult<PopularSearchRecord>;
export type AlgoliaVehicleResult = AlgoliaResult<VehicleRecord>;

export type AlgoliaSearchProductsResponse = AlgoliaSearchResponse<AlgoliaProductResult>;
export type AlgoliaSearchFitmentResponse = AlgoliaSearchResponse<AlgoliaFitmentResult>;
export type AlgoliaSearchVehicleResponse = AlgoliaSearchResponse<AlgoliaVehicleResult>;

// SearchIO
export type BooleanAsString = 'true' | 'false';

export interface IVehicleFilters {
  make?: string;
  model?: string;
  year?: string;
  series?: string;
  bodyType?: string;
}

export type SortOption =
  | 'most_relevant'
  | 'created-descending'
  | 'created-ascending'
  | 'title-ascending'
  | 'title-descending'
  | 'price-ascending'
  | 'price-descending'
  | '';

export interface ISearchParams {
  fields?: string;
  query?: string;
  count?: string;
  countFilters?: string;
  page?: number;
  pipeline?: Record<string, never>;
  resultsPerPage?: number;
  collection?: Collection;
  min?: string;
  max?: string;
  filter?: string;
  vehicleFilters?: IVehicleFilters | Record<string, never>;
  ASKUs?: string;
  showFittingParts?: boolean;
  sort?: string;
  vBoosts1?: string;
  vBoosts2?: string;
  vBoosts3?: string;
  vBoosts4?: string;
  vBoosts5?: string;
  vBoosts6?: string;
  vBoosts7?: string;
  vBoosts8?: string;
  vBoosts9?: string;
  filters?: string;
  facetFilters?: string[][];
  hitsPerPage?: number;
}

export interface ISearchIOAggregation {
  count: {
    counts: ISearchIOAggregationCount;
  };
}

export interface ISearchIOAggregationCount {
  [key: string]: number;
}

export type DefaultItems = unknown[];

export interface ISearchAggregateFilters {
  [filter: string]: ISearchIOAggregation;
}

export interface ISearchResponse<T = DefaultItems> {
  results: T;
  variables: {
    [variable: string]: string;
  };
  totalSize: number;
  numberOfPages: number;
  aggregates: {
    [key: string]: unknown;
  };
  aggregateFilters: ISearchAggregateFilters;
  resultsPerPage: number;
  popularSearches: string[];
  index: string;
  queryID: string;
}

export type SearchioResult<T> = {
  record: T;
  score: number;
  index_score: number;
  neural_score: number;
  feature_score: number;
  relevance_score: number;
  token: unknown;
  banner: unknown;
};

export type FitmentRecord = {
  asku: string;
  criteria: string;
  fittingpositions: string;
  ktypnr: number;
  neural_hash: string;
  panote: string;
  vehicleid: string;
  qtyper: string;
};

export type VehicleRecord = {
  BodyType: string;
  CatalystConverterType: string;
  Chassis: string;
  DriveType: string;
  EngineCode: string;
  EngineType: string;
  FuelMixtureFormation: string;
  FuelType: string;
  KW: string;
  KtypNr: string;
  Hidden: string;
  Lit: string;
  Make: string;
  MakeID: string;
  MapToVehicleID: string;
  Model: string;
  Submodel: string;
  MonthFrom: string;
  MonthTo: string;
  Parent: string;
  PS: string;
  Series: string;
  slugMake: string;
  slugModel: string;
  slugSeries: string;
  VehicleID: string;
  Ventile: string;
  YearFrom: string;
  YearTo: string;
  YearsCount: string;
  Zyl: string;
  _create_time: string;
  _id: string;
  _update_time: string;
  ccmTech: string;
  name: string;
  neural_hash: string;
};

export type ProductRecord = {
  _id: string;
  id: string;
  created_at: string;
  handle: string;
  hasFitment: string;
  title: string;
  vendor: string;
  body_html: string;
  tags: string[];
  collection_ids: string[];
  collection_titles: string[];
  image_height: string;
  image_heights: string[];
  image_ids: string[];
  image_tags: string[];
  image_url: string;
  image_urls: string[];
  image_width: string;
  image_widths: string[];
  inventory_quantity: string;
  max_price: string;
  min_price: string;
  objectID: string;
  option_title: string[];
  position: number;
  product_type: string;
  published_at: string;
  requires_shipping: BooleanAsString;
  status: 'active' | 'draft' | 'archived';
  updated_at: string;
  variant_compare_at_prices: string[];
  variant_ids: string[];
  variant_image_ids: string[];
  variant_options_1: string[];
  variant_options_1_in_stock: string[];
  variant_options_2: string[];
  variant_options_2_in_stock: string[];
  variant_positions: string[];
  variant_prices: string[];
  variant_quantities: string[];
  variant_skus: string[];
  variant_tax_codes: string[];
  variant_titles: string[];
  variant_titles_in_stock: string[];
  variant_weights: string[];
  variants_min_price: number | null;
  variants_max_price: number | null;
  variants_compare_at_price_min: number | null;
  variants_compare_at_price_max: number | null;

  // Not used in all brands
  metafield_plytix_in_store_only?: BooleanAsString;
  metafield_bundles_type?: 'variant' | '';

  Make?: string;
  Model?: string;
  YearFrom?: string;
  ktypnr?: string;
  universal?: string;
  meta?: {
    fitment?: {
      makes: string[];
      models: string[];
      vehicles: number[];
      universal: boolean;
      has_vehicles: boolean;
    };
    unified_commerce?: {
      special_order_dynamic?: boolean;
    };
    plytix?: {
      special_order_required?: boolean;
      backorder_required?: boolean;
      made_to_order?: boolean;
      availableForSale?: boolean;
      part_number?: string;
      has_fitment?: boolean;
      has_variants?: boolean;
      in_store_only?: boolean;
    };
    bundles?: {
      type?: string;
    };
    derivedRelevanceAndUi?: {
      salesLast6Months?: number;
      inStock?: boolean;
    };
  };

  // Optional - @todo revisit (with other brand beyond AMX)
  _create_time?: string;
  _update_time?: string;
  metafield_plytix__sku?: string;
  metafield_plytix_part_number?: string;
  neural_hash3?: string;
  published_scope?: string;
  taxable?: string;
  template_suffix?: string;
  variant_metafield_plytix_product_attributes?: string[];
  variant_metafield_plytix_product_type?: string[];
  variant_metafield_plytix_special_order_required?: string[];
  variant_metafield_plytix_un_number?: string[];
  variant_metafield_plytix_warranty?: string[];
  variant_metafield_plytix_amx_grab_text?: string[];
  variant_metafield_plytix_amx_style?: string[];
  variant_options_3?: string[];
  variant_options_3_in_stock?: string[];
  sku?: string;
  _tags?: string[];
};

export type PopularSearchRecord = {
  query: string;
  popularity: number;
  objectID: string;
  _highlightResult: {
    query: {
      value: string;
    };
  };
};

export type IFitmentResult = SearchioResult<FitmentHit>;
export type IProductResult = SearchioResult<ProductRecord>;

export type ISearchProductsResponse = ISearchResponse<IProductResult[]>;

export enum Collection {
  PRODUCTS,
  VEHICLES,
  FITMENT,
}

export const UserVehicleSelectionKey = 'userSelections';

export interface FacetStats {
  avg: number;
  max: number;
  min: number;
  sum: number;
}
